/**
 * Logo Carousel Component
 * 
 * Displays a set of partner logos in a continuously sliding carousel
 * that pauses on hover and is fully responsive.
 * Supports touch drag on mobile devices.
 */

import { setAriaLabel } from '../Utils/accessibility-utils';

export interface LogoItem {
    /**
     * SVG markup for the logo
     */
    svg: string;

    /**
     * Alt text for accessibility
     */
    alt?: string;
}

export interface LogoCarouselOptions {
    /**
     * Title to display above the logo carousel
     */
    title?: string;

    /**
     * Array of logo objects to display in the carousel
     */
    logos?: LogoItem[];

    /**
     * Animation duration in seconds (default: 30s for desktop, 180s for mobile)
     */
    animationDuration?: number;

    /**
     * Optional class name for custom styling
     */
    className?: string;
}
// Track event listeners for proper cleanup
interface EventListenerEntry {
    target: EventTarget;
    type: string;
    listener: EventListenerOrEventListenerObject;
    options?: boolean | AddEventListenerOptions;
}

const CONSTANTS = {
    MOBILE_BREAKPOINT: 768,
    LOGO_GAP: 24, // Must match CSS gap value in logo-carousel.less
    CLONE_COUNT: 3, // Number of times to clone logos for infinite scrolling
    ANIMATION_DELAY: 100, // Milliseconds to wait before starting animation
    DEFAULT_ANIMATION_DURATION: 30
};

// SVG templates to avoid duplication 
const SVG_TEMPLATES = {
    PAUSE: `<svg xmlns="http://www.w3.org/2000/svg" width="33" height="32" viewBox="0 0 33 32" fill="currentColor">
    <path d="M16.5 0C7.66 0 0.5 7.16 0.5 16C0.5 24.84 7.66 32 16.5 32C25.34 32 32.5 24.84 32.5 16C32.5 7.16 25.34 0 16.5 0ZM14.74 20C14.74 21 13.94 21.76 12.98 21.76C11.98 21.76 11.22 20.96 11.22 20V12C11.18 11.04 11.98 10.24 12.94 10.24C13.94 10.24 14.74 11.04 14.74 12V20ZM21.82 20C21.82 21 21.02 21.76 20.06 21.76C19.06 21.76 18.3 20.96 18.3 20V12C18.26 11.04 19.06 10.24 20.02 10.24C21.02 10.24 21.82 11.04 21.82 12V20Z" fill="currentColor"/>
  </svg>`,
    PLAY: `
    <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_4235_8655)">
      <path d="M16.5 0C7.66356 0 0.5 7.16344 0.5 16C0.5 24.8366 7.66356 32 16.5 32C25.3364 32 32.5 24.8366 32.5 16C32.5 7.16344 25.3364 0 16.5 0ZM22.03 16.8481L14.03 21.8481C13.8681 21.9492 13.6841 22 13.5 22C13.3333 22 13.1663 21.9585 13.0151 21.8745C12.6973 21.6983 12.5 21.3638 12.5 21V11C12.5 10.6362 12.6973 10.3017 13.0151 10.1255C13.333 9.94825 13.7217 9.959 14.03 10.1519L22.03 15.1519C22.3223 15.335 22.5 15.6553 22.5 16C22.5 16.3447 22.3223 16.6651 22.03 16.8481Z" fill="currentColor"/>
      </g>
      <defs>
      <clipPath id="clip0_4235_8655">
      <rect width="32" height="32" fill="white" transform="translate(0.5)"/>
      </clipPath>
      </defs>
    </svg>
  `
};

// Helper to get the appropriate play/pause SVG
const getPlayPauseSVG = (isPlaying: boolean): string => {
    return isPlaying ? SVG_TEMPLATES.PAUSE : SVG_TEMPLATES.PLAY;
};

type AnimationState = 'running' | 'paused';
type CarouselMode = 'static' | 'scrolling';
type DeviceMode = 'mobile' | 'desktop';

export class LogoCarousel {
    private element: HTMLElement;
    private animationDuration?: number | null
    private state: {
        isDragging: boolean;
        startX: number;
        scrollLeft: number;
        animationState: AnimationState;
        deviceMode: DeviceMode;
        carouselMode: CarouselMode;
        eventListeners: Map<string, EventListenerEntry[]>;
        styleElements: Set<HTMLStyleElement>;
        instanceId: string;
        carouselRoot: HTMLElement;
        isPlaying: boolean;
    };

    private elements: {
        getTrackElements: () => NodeListOf<HTMLElement>;
        getSliderElement: () => HTMLElement | null;
        getLogoItems: () => NodeListOf<HTMLElement>;
        getPlayPauseButton: () => HTMLButtonElement | null;
        getTitleDiv: () => HTMLElement | null;
    };

    constructor(element: HTMLElement, animationDuration?: number) {
        this.element = element;
        this.animationDuration = animationDuration;

        this.state = {
            isDragging: false,
            startX: 0,
            scrollLeft: 0,
            animationState: 'running' as AnimationState,
            deviceMode: 'desktop' as DeviceMode,
            carouselMode: 'static' as CarouselMode,
            eventListeners: new Map<string, EventListenerEntry[]>(),
            styleElements: new Set<HTMLStyleElement>(),
            instanceId: Date.now() + '-' + Math.floor(Math.random() * 10000), // For unique animation names
            carouselRoot: this.element, // Store reference to root container for scoped queries
            isPlaying: true, // Track whether carousel is currently playing or paused
        };

        // DOM element selectors - gathered once to avoid repeated DOM queries
        this.elements = {
            getTrackElements: () => this.element.querySelectorAll('.logo-carousel__track') as NodeListOf<HTMLElement>,
            getSliderElement: () => this.element.querySelector('.logo-carousel__slider') as HTMLElement,
            getLogoItems: () => this.element.querySelectorAll('.logo-carousel__track .logo-carousel__item') as NodeListOf<HTMLElement>,
            getPlayPauseButton: () => this.element.querySelector('.logo-carousel__play-pause') as HTMLButtonElement | null,
            getTitleDiv: () => this.element.querySelector('.logo-carousel__title') as HTMLElement | null
        };

        this.init();
    }

    // Helper to safely add event listeners and track them for cleanup
    private addEventListenerWithCleanup = (
        target: EventTarget,
        type: string,
        listener: EventListenerOrEventListenerObject,
        options?: boolean | AddEventListenerOptions
        ) => {
        target.addEventListener(type, listener, options);

        const id = target === window ? 'window' :
            target === document ? 'document' : 'element';

        if (!this.state.eventListeners.has(id)) {
            this.state.eventListeners.set(id, []);
        }

        this.state.eventListeners.get(id)?.push({
            target,
            type,
            listener,
            options
        });
    };

    // Helper to safely create style elements and track them for cleanup
    private createStyleElement = (cssText: string) => {
        const styleElement = document.createElement('style');
        styleElement.textContent = cssText;
        document.head.appendChild(styleElement);
        this.state.styleElements.add(styleElement);
        return styleElement;
    };

    private detectDeviceMode = () => {
        const newDeviceMode = window.innerWidth < CONSTANTS.MOBILE_BREAKPOINT ? 'mobile' : 'desktop';

        if (newDeviceMode !== this.state.deviceMode) {
            this.state.deviceMode = newDeviceMode;
            return true;
        }

        return false;
    };

    // Layout detection - determine if logos are cut off screen
    private determineCarouselMode = () => {
        const logoItems = this.elements.getLogoItems();
        if (!logoItems.length) return false;

        const logoWidth = this.measureLogoItemWidth(logoItems[0]);
        const logoGap = CONSTANTS.LOGO_GAP;
        const totalLogoWidth = logoWidth + logoGap;

        if (logoWidth === 0) {
            console.warn('Logo width measurement failed, defaulting to scrolling mode');
            this.state.carouselMode = 'scrolling';
            return true;
        }

        // Calculate how many logos can fit on screen
        const visibleWidth = window.innerWidth;
        const logosPerScreen = Math.floor(visibleWidth / totalLogoWidth);

        const needsScrolling = logoItems.length > logosPerScreen;

        this.state.carouselMode = needsScrolling ? 'scrolling' : 'static';
        return needsScrolling;
    };

    // Accurately measure a logo item's width, handling initial render edge cases
    private measureLogoItemWidth = (logoItem: HTMLElement): number => {
        if (!logoItem) return 0;

        let width = logoItem.getBoundingClientRect().width;

        // If width is 0, try forcing a reflow and measure again
        if (width === 0) {
            const computedStyle = window.getComputedStyle(logoItem);
            if (computedStyle.display === 'none') {
                console.warn('Logo item has display:none, measurement may be inaccurate');
            }

            // Force a reflow and measure again
            void logoItem.offsetWidth;
            width = logoItem.getBoundingClientRect().width;
        }

        return width;
    };

    private setupStaticMode = () => {
        const track = this.elements.getTrackElements()[0];
        if (!track) return;

        const button = this.elements.getPlayPauseButton();

        if (button) {
            button.remove();
        }

        // Center logos and disable animation
        track.style.animation = 'none';
        track.style.justifyContent = 'center';
        track.style.transform = 'translateX(0)';
        track.classList.add('logo-carousel__track--static');
    };

    private setupScrollingMode = () => {
        const trackElements = this.elements.getTrackElements();
        const sliderElement = this.elements.getSliderElement();

        if (!trackElements.length || !sliderElement) return;

        // Clone the track for infinite scroll illusion
        const originalTrack = trackElements[0];
        const duplicateTrack = originalTrack.cloneNode(true) as HTMLElement;
        sliderElement.appendChild(duplicateTrack);

        // Configure all tracks for animation
        const allTracks = this.elements.getTrackElements();
        allTracks.forEach(track => {
            track.classList.remove('logo-carousel__track--static');
            track.style.justifyContent = '';
        });

        this.setupInitialAnimations();

        if (this.state.deviceMode === 'mobile') {
            this.setupMobileInteractions(sliderElement);
        } else {
            this.setupHoverInteractions();
        }

        this.addPlayPauseButton();
    };

    // Apply initial animations with speed adjusted for number of logos
    private setupInitialAnimations = () => {
        const tracks = this.elements.getTrackElements();

        tracks.forEach((track, index) => {
            // Set up infinite scroll clones (needed for both desktop and mobile)
            const items = track.querySelectorAll('.logo-carousel__item');
            if (!items.length) return;

            this.setupClonesForTrack(track);

            // Get current position
            const computedStyle = window.getComputedStyle(track);
            const transformMatrix = new DOMMatrix(computedStyle.transform);
            const startPosition = transformMatrix.m41 || 0;

            // Apply animation with adjusted duration based on number of logos
            this.createContinuousAnimation(track, startPosition, index);
        });
    };

    private setupHoverInteractions = () => {
        const sliderElements = this.element.querySelectorAll('.logo-carousel__slider');

        sliderElements.forEach(slider => {
            // Pause animation on hover only if carousel is currently playing
            this.addEventListenerWithCleanup(slider, 'mouseenter', () => {
                if (this.state.isPlaying) {
                    const tracks = Array.from(slider.querySelectorAll('.logo-carousel__track')) as HTMLElement[];
                    tracks.forEach(track => {
                        track.style.animationPlayState = 'paused';
                    });
                }
            });

            // Resume animation when not hovering - only if the carousel is set to play
            this.addEventListenerWithCleanup(slider, 'mouseleave', () => {
                if (this.state.isPlaying) {
                    const tracks = Array.from(slider.querySelectorAll('.logo-carousel__track')) as HTMLElement[];
                    tracks.forEach(track => {
                        track.style.animationPlayState = 'running';
                    });
                }
            });
        });
    };

    private setupClonesForTrack = (track: HTMLElement) => {
        const items = track.querySelectorAll('.logo-carousel__item');
        if (!items.length) return;

        // Only set up clones if they haven't been set up already
        if (track.dataset.clonesSetup === 'true') return;

        // Create document fragment with clones
        const itemsFragment = document.createDocumentFragment();

        // Create multiple sets of clones for smooth infinite effect
        for (let i = 0; i < CONSTANTS.CLONE_COUNT; i++) {
            items.forEach(item => {
                const clone = item.cloneNode(true);
                itemsFragment.appendChild(clone);
            });
        }

        // Add clones to beginning and end of track
        track.prepend(itemsFragment.cloneNode(true));
        track.appendChild(itemsFragment);

        // Position track with original items visible
        const itemWidth = this.measureLogoItemWidth(items[0] as HTMLElement);
        const totalItemWidth = itemWidth + CONSTANTS.LOGO_GAP;
        const originalItemsCount = items.length;

        // Center the original items
        track.style.transform = `translateX(${-originalItemsCount * totalItemWidth * CONSTANTS.CLONE_COUNT}px)`;

        // Mark this track as having clones set up
        track.dataset.clonesSetup = 'true';
    };

    private setupMobileInteractions = (sliderElement: HTMLElement) => {
        this.setupInfiniteScrollClones();
        this.setupTouchDragEvents(sliderElement);
    };

    // Clone logos for mobile infinite dragging effect
    private setupInfiniteScrollClones = () => {
        const tracks = this.elements.getTrackElements();

        tracks.forEach(track => {
            const items = track.querySelectorAll('.logo-carousel__item');
            if (!items.length) return;

            // Create document fragment with clones
            const itemsFragment = document.createDocumentFragment();

            // Create multiple sets of clones for smooth infinite dragging effect
            for (let i = 0; i < CONSTANTS.CLONE_COUNT; i++) {
                items.forEach(item => {
                    const clone = item.cloneNode(true);
                    itemsFragment.appendChild(clone);
                });
            }

            // Add clones to beginning and end of track
            track.prepend(itemsFragment.cloneNode(true));
            track.appendChild(itemsFragment);

            // Position track with original items visible
            const itemWidth = this.measureLogoItemWidth(items[0] as HTMLElement);
            const totalItemWidth = itemWidth + CONSTANTS.LOGO_GAP;
            const originalItemsCount = items.length;

            // Center the original items
            track.style.transform = `translateX(${-originalItemsCount * totalItemWidth * CONSTANTS.CLONE_COUNT}px)`;
        });
    };

    private setupTouchDragEvents = (sliderElement: HTMLElement) => {
        // Touch start - begin dragging
        this.addEventListenerWithCleanup(sliderElement, 'touchstart', (e: Event) => {
            if (this.state.deviceMode !== 'mobile') return;

            const touchEvent = e as TouchEvent;
            this.state.isDragging = true;
            this.state.startX = touchEvent.touches[0].pageX;

            this.pauseAnimationsAndStorePosition();

            touchEvent.preventDefault();
        }, { passive: false });

        // Touch move - drag the carousel
        this.addEventListenerWithCleanup(sliderElement, 'touchmove', (e: Event) => {
            if (!this.state.isDragging) return;

            const touchEvent = e as TouchEvent;
            const x = touchEvent.touches[0].pageX;
            const walk = x - this.state.startX;

            this.applyDragMovement(walk);

            touchEvent.preventDefault();
        }, { passive: false });

        // Touch end - resume animation from new position
        this.addEventListenerWithCleanup(sliderElement, 'touchend', () => {
            if (!this.state.isDragging) return;
            this.state.isDragging = false;

            this.resumeAnimationFromCurrentPosition();
        });
    };

    private pauseAnimationsAndStorePosition = () => {
        const tracks = this.elements.getTrackElements();
        tracks.forEach(track => {
            // Get current position from computed style
            const computedStyle = window.getComputedStyle(track);
            const transformMatrix = new DOMMatrix(computedStyle.transform);
            this.state.scrollLeft = transformMatrix.m41; // X translation value

            // Pause animation and set explicit transform
            track.style.animation = 'none';
            track.style.transform = `translateX(${this.state.scrollLeft}px)`;
        });
    };

    private applyDragMovement = (walkDistance: number) => {
        const tracks = this.elements.getTrackElements();
        tracks.forEach(track => {
            const newPosition = this.state.scrollLeft + walkDistance;
            track.style.transform = `translateX(${newPosition}px)`;
        });
    };

    private resumeAnimationFromCurrentPosition = () => {
        const tracks = this.elements.getTrackElements();

        tracks.forEach((track, index) => {
            // Get current position
            const computedStyle = window.getComputedStyle(track);
            const transformMatrix = new DOMMatrix(computedStyle.transform);
            let currentX = transformMatrix.m41;

            // Apply position wrapping for infinite effect
            currentX = this.wrapPosition(currentX, track);

            // Apply wrapped position
            track.style.transform = `translateX(${currentX}px)`;

            // Force a reflow before changing animation
            void track.offsetWidth;

            this.createContinuousAnimation(track, currentX, index);
        });
    };

    // Wrap position for infinite scrolling effect
    private wrapPosition = (position: number, track: HTMLElement): number => {
        const items = track.querySelectorAll('.logo-carousel__item');
        if (!items.length) return position;

        const itemWidth = this.measureLogoItemWidth(items[0] as HTMLElement);
        const totalItemWidth = itemWidth + CONSTANTS.LOGO_GAP;
        const sectionWidth = items.length / (1 + CONSTANTS.CLONE_COUNT * 2) * totalItemWidth;

        // Create thresholds for wrapping
        const leftThreshold = -sectionWidth * (CONSTANTS.CLONE_COUNT + 2);
        const rightThreshold = -sectionWidth * (CONSTANTS.CLONE_COUNT - 1);

        if (position < leftThreshold) {
            // If scrolled too far left, wrap to right
            return position + sectionWidth * CONSTANTS.CLONE_COUNT;
        } else if (position > rightThreshold) {
            // If scrolled too far right, wrap to left
            return position - sectionWidth * CONSTANTS.CLONE_COUNT;
        }

        return position;
    };

    private createContinuousAnimation = (track: HTMLElement, startPosition: number, index: number) => {
        const items = track.querySelectorAll('.logo-carousel__item');
        if (!items.length) return;

        const itemWidth = this.measureLogoItemWidth(items[0] as HTMLElement);
        const totalItemWidth = itemWidth + CONSTANTS.LOGO_GAP;
        const sectionWidth = items.length / (1 + CONSTANTS.CLONE_COUNT * 2) * totalItemWidth;

        // Calculate duration based on number of items to ensure consistent speed
        // This approach adjusts duration proportionally to the number of visible items
        // so each carousel moves at visually the same speed regardless of item count
        const itemCount = items.length / (1 + CONSTANTS.CLONE_COUNT * 2);

        // Base duration calibrated to a reference of 6 items
        const referenceItemCount = 6;
        let baseDuration: number;

        if (this.animationDuration) {
            // Custom duration is applied proportionally
            baseDuration = this.animationDuration;
        }
        else {
            baseDuration = CONSTANTS.DEFAULT_ANIMATION_DURATION;
        }

        // Scale duration proportionally to item count
        // More items = longer duration to maintain same visual speed
        const duration = baseDuration * (itemCount / referenceItemCount);

        // Create a unique animation name for this carousel instance and track
        const animationName = `resumeCarousel_${this.state.instanceId}_${index}`;

        const keyframes = `
      @keyframes ${animationName} {
        0% {
          transform: translateX(${startPosition}px);
        }
        100% {
          transform: translateX(${startPosition - sectionWidth}px);
        }
      }
    `;

        this.createStyleElement(keyframes);

        // Apply animation with a small delay
        setTimeout(() => {
            track.style.animation = `${animationName} ${duration}s linear infinite`;
            track.style.animationPlayState = this.state.animationState;
        }, CONSTANTS.ANIMATION_DELAY);
    };

    private setupResizeHandler = () => {
        const handleResize = () => {
            const deviceModeChanged = this.detectDeviceMode();
            const needsScrolling = this.determineCarouselMode();

            // If device mode changed or carousel mode changed, reinitialize
            if (deviceModeChanged ||
                (needsScrolling && this.state.carouselMode === 'static') ||
                (!needsScrolling && this.state.carouselMode === 'scrolling')) {

                this.cleanup();

                this.init();
            }
        };

        let resizeTimeout: number | null = null;
        const debouncedResize = () => {
            if (resizeTimeout) {
                window.clearTimeout(resizeTimeout);
            }
            resizeTimeout = window.setTimeout(handleResize, 150);
        };

        this.addEventListenerWithCleanup(window, 'resize', debouncedResize);
    };

    private init = () => {
        this.detectDeviceMode();
        const needsScrolling = this.determineCarouselMode();

        if (needsScrolling) {
            this.setupScrollingMode();
        } else {
            this.setupStaticMode();
        }

        this.setupResizeHandler();
    };

    // Clean up event listeners and dynamically created elements
    private cleanup = () => {
        this.state.eventListeners.forEach(listeners => {
            listeners.forEach(({ target, type, listener, options }) => {
                target.removeEventListener(type, listener, options);
            });
        });

        this.state.eventListeners.clear();

        // Remove all dynamically created style elements
        this.state.styleElements.forEach(styleElement => {
            if (styleElement.parentNode) {
                styleElement.parentNode.removeChild(styleElement);
            }
        });

        this.state.styleElements.clear();
    };

    /**
     * Updates the play/pause button's appearance and functionality
     * Based on current state of animation
     */
    private updatePlayPauseButton = (button?: HTMLButtonElement | null) => {
        // Use provided button or query for it if not provided
        const targetButton = button || this.elements.getPlayPauseButton();
        if (!targetButton) return;

        // Update icon 
        const iconContainer = targetButton.querySelector('.logo-carousel__play-pause-icon');
        if (iconContainer) {
            iconContainer.className = "logo-carousel__play-pause-icon";
            iconContainer.innerHTML = getPlayPauseSVG(this.state.isPlaying);
        }
    };

    private updateTracksPlayState = () => {
        const tracks = this.elements.getTrackElements();
        tracks.forEach(track => {
            track.style.animationPlayState = this.state.animationState;
        });
    };

    private togglePlayPauseState = () => {
        this.state.isPlaying = !this.state.isPlaying;
        this.state.animationState = this.state.isPlaying ? 'running' : 'paused';

       this.updateTracksPlayState();

        this.updatePlayPauseButton();
    };

    private addPlayPauseButton = () => {
        const button = this.elements.getPlayPauseButton();

        if (button) {
            this.addEventListenerWithCleanup(button, 'click', this.togglePlayPauseState);
            return; // Button already added, no need to add again
        }
    };
};

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.logo-carousel').forEach(element => {
        new LogoCarousel(element as HTMLElement);
    });
});